import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PayPalButtons } from "@paypal/react-paypal-js";
import { api } from "../lib/api";
import { Coins, CheckCircle2, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function PennyTest() {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);

  const createOrder = async () => {
    try {
      const r = await api.post("/paypal/penny-test", { origin_url: window.location.origin });
      return r.data.order_id;
    } catch (e) {
      toast.error("Could not start penny test");
      throw e;
    }
  };

  const onApprove = async (data) => {
    setBusy(true);
    try {
      const r = await api.post(`/paypal/capture-order/${data.orderID}`);
      nav(`/payment-success?order_id=${r.data.order_id}`);
    } catch (e) {
      toast.error("Capture failed — check backend logs");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen max-w-2xl mx-auto px-4 md:px-8 py-12 md:py-20" data-testid="penny-test-page">
      <Link to="/" className="inline-flex items-center gap-2 text-[#8D9688] hover:text-[#DD5422] uppercase tracking-widest text-xs font-anton mb-6">
        <ArrowLeft className="w-4 h-4" /> Back Home
      </Link>

      <div className="bg-[#151A14] border-2 border-[#2C332A] p-8 md:p-12">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 bg-[#DD5422] flex items-center justify-center">
            <Coins className="w-7 h-7 text-[#0A0E0B]" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-anton uppercase tracking-[0.3em] text-[#DD5422] text-xs">/ Owner Tool</div>
            <h1 className="font-anton text-3xl md:text-4xl uppercase tracking-tight">Penny Test</h1>
          </div>
        </div>

        <p className="text-[#E5E5E5] mb-4">
          One click. <span className="text-[#DD5422] font-anton text-2xl">$0.01</span> charge to verify the whole pipeline works end-to-end:
        </p>

        <ol className="text-sm text-[#8D9688] space-y-2 mb-6 ml-4 list-decimal list-inside">
          <li>PayPal collects your shipping address</li>
          <li>Backend captures the $0.01 payment</li>
          <li>A <strong className="text-[#E5E5E5]">draft order</strong> appears in your Printful dashboard</li>
          <li>You delete the Printful draft (nothing gets shipped)</li>
          <li>You refund yourself the penny in PayPal (or don't, it's a penny)</li>
        </ol>

        <div className="bg-[#0A0E0B] border-2 border-[#4B5320] p-4 mb-6">
          <div className="text-xs uppercase tracking-widest text-[#8D9688] mb-1">Total Cost To You</div>
          <div className="font-anton text-3xl text-[#DD5422]">~$0.01</div>
          <div className="text-xs text-[#8D9688] mt-1">(plus PayPal's tiny processing fee, money moves you → you)</div>
        </div>

        {busy ? (
          <div className="text-center text-[#DD5422] uppercase tracking-widest font-anton" data-testid="penny-test-busy">
            Confirmin'...
          </div>
        ) : (
          <div data-testid="penny-test-buttons">
            <PayPalButtons
              style={{ layout: "vertical", shape: "rect", color: "gold", label: "paypal", height: 48 }}
              createOrder={createOrder}
              onApprove={onApprove}
              onCancel={() => toast.info("Cancelled")}
              onError={() => toast.error("PayPal hiccup — try again")}
              forceReRender={["penny-test"]}
            />
          </div>
        )}

        <div className="mt-6 text-xs text-[#8D9688] uppercase tracking-widest text-center">
          <CheckCircle2 className="w-3 h-3 inline mr-1" />
          Real money. Real Printful order. Just $0.01.
        </div>
      </div>

      <div className="mt-6 text-center">
        <Link to="/" className="text-[#8D9688] hover:text-[#DD5422] text-sm uppercase tracking-widest">
          ← Done testing? Back to the shop
        </Link>
      </div>
    </div>
  );
}
